import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import CustomTooltip from "../../Elements/Tooltip";
import Select from "../../Elements/Select";
import "../../../../assets/stylesheets/flags.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestion } from "@fortawesome/free-solid-svg-icons";
import styles from "./links.module.css";
import Input from "../../Elements/Input";
import noImage from "../../../../assets/images/icon_upload.svg";
import { networkService } from "../../config/axios";
import { resolveTags } from "../../../utils/resolveTags";
import * as XLSX from "xlsx";
import CustomTagsInput from "../../CommonComponents/Tags/CustomTagsInput";
import TagChipEditor from "../../CommonComponents/Tags/TagChipEditor";
import CustomDropdown from "../../CommonComponents/CustomDropDown/CustomDropDown";


const LaserLINKSettingsTab = ({
  editLaserLinkItem,
  handleOnchangeEvent,
  updateEditLaserLinkData,
  getDomains,
  getDocFiles,
  validationTrigger,
  updateErrorStatus,
  errorListData,
  batchStatus = null,
  setSaveButtonStatus,
  batchFile,
  setBatchFile,
  setIsError
}) => {
  const [filteredCampaign, setFilteredCampaign] = useState("");
  const [campaignListData, setCampaignListData] = useState([]);
  const [laserLinkType, setLaserLinkType] = useState("individual");

  const disableSettings = editLaserLinkItem?.disable_settings ?? {};
  const delayOptionSelected = disableSettings?.disable_on === "seconds";
  const delaySeconds = Number(disableSettings?.disable_for ?? 0);
  const shouldShowRecipientNotification = delayOptionSelected && delaySeconds > 0;
  const canShowEmbedOptions = Boolean(editLaserLinkItem?.tracking_link);

  // Opt-in page value: 3 when "3rd page", otherwise custom show_on_page
  const getOptInPageNumber = useCallback(() => {
    if (!editLaserLinkItem?.has_optin) return null;
    if (editLaserLinkItem?.opt_in?.optin_mode === "pages_viewed") return null;
    if (editLaserLinkItem?.optin_2nd_page_viewed === true) return 3;
    return Number(editLaserLinkItem?.opt_in?.show_on_page || 0) || null;
  }, [editLaserLinkItem?.has_optin, editLaserLinkItem?.optin_2nd_page_viewed, editLaserLinkItem?.opt_in?.show_on_page, editLaserLinkItem?.opt_in?.optin_mode]);

  // Keep landing_page_gt_optin in sync when opt-in or landing page changes
  useEffect(() => {
    const optInPage = getOptInPageNumber();
    const landingNum = Number(editLaserLinkItem?.landing_page);
    const isLandingGtOptIn = optInPage != null && optInPage >= 1 && !Number.isNaN(landingNum) && landingNum > optInPage;
    const current = errorListData || [];
    const next = [...current.filter((k) => k !== "landing_page_gt_optin")];
    if (isLandingGtOptIn) next.push("landing_page_gt_optin");
    const changed = next.length !== current.length || next.some((k, i) => k !== current[i]);
    if (changed) updateErrorStatus(next);
  }, [
    editLaserLinkItem?.landing_page,
    editLaserLinkItem?.has_optin,
    editLaserLinkItem?.optin_2nd_page_viewed,
    editLaserLinkItem?.opt_in?.show_on_page,
    getOptInPageNumber,
    errorListData,
    updateErrorStatus,
  ]);

  const sanitizeDelaySecondsInput = useCallback((value) => {
    if (value === "" || value === null || value === undefined) {
      return "";
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return "";
    }

    return Math.max(1, numericValue);
  }, []);

  const handleDelaySecondsKeyDown = useCallback((event) => {
    if (event.key === "ArrowDown" || event.key === "Down") {
      const currentValue = Number(event.currentTarget.value ?? 0);
      if (currentValue <= 1) {
        event.preventDefault();
      }
    }
  }, []);

  const handleDelaySecondsWheel = useCallback((event) => {
    const currentValue = Number(event.currentTarget.value ?? 0);
    if (currentValue <= 1 && event.deltaY > 0) {
      event.preventDefault();
    }
  }, []);

  const handleDelaySecondsBlur = useCallback(() => {
    if (
      disableSettings?.disable_on === "seconds" &&
      (disableSettings?.disable_for === "" ||
        disableSettings?.disable_for === null ||
        disableSettings?.disable_for === undefined)
    ) {
      updateEditLaserLinkData({
        ...editLaserLinkItem,
        disable_settings: {
          ...editLaserLinkItem.disable_settings,
          disable_for: 1,
        },
      });
    }
  }, [disableSettings?.disable_for, disableSettings?.disable_on, editLaserLinkItem, updateEditLaserLinkData]);

  useEffect(() => {
    if (!editLaserLinkItem?.disable_settings) return;

    const currentValue = Number(disableSettings?.disable_for ?? 0);
    if (!currentValue || currentValue < 1) {
      updateEditLaserLinkData({
        ...editLaserLinkItem,
        disable_settings: {
          ...editLaserLinkItem.disable_settings,
          disable_for: 1,
        },
      });
    }
  }, [disableSettings?.disable_for, editLaserLinkItem, updateEditLaserLinkData]);


    const handleUpdateCampaign = async (value) => {
    if (value != undefined && value?.trim() != "") {
      let campaignValueToUpdate = {};
      let campaignExist = campaignListData?.filter(
        (item) => item?.name == value
      );
      if (campaignExist?.length > 0) {
        campaignValueToUpdate = {
          campaign: campaignExist[0].name,
          campaign_id: campaignExist[0].id,
        };
      } else {
        let campaignPayload = { name: value };
        let { data } = await networkService(
          `campaigns.json`,
          "POST",
          campaignPayload
        );
        campaignValueToUpdate = { campaign: data, campaign_id: data.id };
      }
      updateEditLaserLinkData({
        ...editLaserLinkItem,
        ...campaignValueToUpdate,
      });
      setFilteredCampaign("");
      updateErrorStatus(errorListData?.filter((it) => it != "campaign"));
    } else {
      updateEditLaserLinkData({
        ...editLaserLinkItem,
        campaign: null,
        campaign_id: null,
      });
    }
  };

  const handleTagsChange = async (nextTags) => {
    const existingTags = Array.isArray(editLaserLinkItem?.tags)
      ? editLaserLinkItem.tags
      : [];
    const resolvedTags = await resolveTags(nextTags, existingTags);
    updateEditLaserLinkData({
      ...editLaserLinkItem,
      tags: resolvedTags,
      tag_ids: resolvedTags.map((tag) => tag.id),
    });
  };

  const handleValidateBatchFile = (fileGet) => {
    if (fileGet) {
        const fileType = fileGet.type;
        const fileName = fileGet.name;
        if (
            fileType === "application/vnd.ms-excel" ||
            fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const binaryStr = event.target.result;
                const workbook = XLSX.read(binaryStr, { type: "binary" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const data = XLSX.utils.sheet_to_json(worksheet);
                
                updateEditLaserLinkData({
                    ...editLaserLinkItem,
                    batch_link_array: data,
                    file_name: fileName,
                });

                setBatchFile(fileGet);
                setSaveButtonStatus(false);
            };
            reader.onerror = () => {
                setBatchFile(false);
            };
            reader.readAsBinaryString(fileGet);
        } else {
            setBatchFile(false);
        }
    }
};

  // const validateInnerData = () => {
  //   let errorToUpdate = JSON.parse(JSON.stringify(errorListData));

  //   // Validate campaign for "public" laserLinkType
  //   if (laserLinkType === "public" && editLaserLinkItem?.campaign_id == null) {
  //     if (!errorToUpdate?.includes("campaign")) {
  //       errorToUpdate.push("campaign");
  //     }
  //   } else {
  //     errorToUpdate = errorToUpdate?.filter((it) => it !== "campaign");
  //   }

  //   // Validate notify_when_page conditions
  //   if (
  //     editLaserLinkItem?.receipient_type === "notify_when_page" &&
  //     (
  //       editLaserLinkItem?.notify_for_page <= 0 ||
  //       editLaserLinkItem?.notify_for_page > getDocFiles[0]?.num_pages ||
  //       editLaserLinkItem?.notify_page_after < 0 ||
  //       editLaserLinkItem?.notify_page_after == ''
  //     )
  //   ) {
  //     if (!errorToUpdate?.includes("notify_page")) {
  //       errorToUpdate.push("notify_page");
  //     }
  //   } else {
  //     errorToUpdate = errorToUpdate?.filter((it) => it !== "notify_page");
  //   }

  //   // Validate batch file
  //   if (batchFile === false) {
  //     if (!errorToUpdate?.includes("batch_link")) {
  //       errorToUpdate.push("batch_link");
  //     }
  //   } else {
  //     errorToUpdate = errorToUpdate?.filter((it) => it !== "batch_link");
  //   }

  //   // Update error status
  //   updateErrorStatus([...errorToUpdate]);
  // };

  const validateInnerData = () => {
  let errorToUpdate = Array.isArray(errorListData) ? [...errorListData] : [];

  // public link ⇒ campaign required
  if (laserLinkType === "public" && !editLaserLinkItem?.campaign_id) {
    if (!errorToUpdate.includes("campaign")) errorToUpdate.push("campaign");
  } else {
    errorToUpdate = errorToUpdate.filter(k => k !== "campaign");
  }

  // notify_when ⇒ notify_after >= 1 required
  const notifyAfterValue = editLaserLinkItem?.notify_after;
  const notifyAfter = Number(notifyAfterValue ?? 0);
  if (editLaserLinkItem?.receipient_type === "notify_when" &&
      (notifyAfterValue === '' || notifyAfterValue === null || notifyAfterValue === undefined || notifyAfter < 1)) {
    if (!errorToUpdate.includes("notify_after")) errorToUpdate.push("notify_after");
  } else {
    errorToUpdate = errorToUpdate.filter(k => k !== "notify_after");
  }

  // notify_when_page ⇒ page bounds (1 to max) + seconds >= 1 required
  const pages = getDocFiles?.[0]?.num_pages || 0;
  const notifyForPageValue = editLaserLinkItem?.notify_for_page;
  const notifyPageAfterValue = editLaserLinkItem?.notify_page_after;
  const onPage = Number(notifyForPageValue ?? 0);
  const secs = Number(notifyPageAfterValue ?? 0);

  if (editLaserLinkItem?.receipient_type === "notify_when_page") {
    // Validate notify_for_page: must be between 1 and max pages, and not empty
    if (notifyForPageValue === '' || notifyForPageValue === null || notifyForPageValue === undefined || 
        onPage < 1 || onPage > pages) {
      if (!errorToUpdate.includes("notify_for_page")) errorToUpdate.push("notify_for_page");
    } else {
      errorToUpdate = errorToUpdate.filter(k => k !== "notify_for_page");
    }

    // Validate notify_page_after: must be >= 1 and not empty
    if (notifyPageAfterValue === '' || notifyPageAfterValue === null || notifyPageAfterValue === undefined || secs < 1) {
      if (!errorToUpdate.includes("notify_page_after")) errorToUpdate.push("notify_page_after");
    } else {
      errorToUpdate = errorToUpdate.filter(k => k !== "notify_page_after");
    }

    // Keep notify_page error for backward compatibility if either field has error
    if (errorToUpdate.includes("notify_for_page") || errorToUpdate.includes("notify_page_after")) {
      if (!errorToUpdate.includes("notify_page")) errorToUpdate.push("notify_page");
    } else {
      errorToUpdate = errorToUpdate.filter(k => k !== "notify_page");
    }
  } else {
    // Clear all notify_when_page related errors if option is not selected
    errorToUpdate = errorToUpdate.filter(k => !["notify_page", "notify_for_page", "notify_page_after"].includes(k));
  }

  // Save-time "no MultiLink file" error clears once a file is provided.
  if (batchFile || editLaserLinkItem?.batch_link_array?.length) {
    errorToUpdate = errorToUpdate.filter(k => k !== "batch_file");
  }

  // batch multi-link ⇒ invalid file
  if (batchFile === false) {
    if (!errorToUpdate.includes("batch_link")) errorToUpdate.push("batch_link");
  } else {
    errorToUpdate = errorToUpdate.filter(k => k !== "batch_link");
  }

  updateErrorStatus(errorToUpdate);
  setIsError?.(errorToUpdate.length > 0);
};

  
  const handleDefaultSettings = async () => {
    const { data } = await networkService("campaigns.json", "GET");
    if (data?.length > 0) {
      setCampaignListData(data);
    }
  };

  useEffect(() => {
    handleDefaultSettings();
  }, []);

  useEffect(() => {
    if (editLaserLinkItem?.campaign_id != null) {
      setLaserLinkType("public");
    }
  }, [editLaserLinkItem?.campaign_id]);

  // Validate live (not only on Save) so the tab warning icon and disabled Save reflect errors as the user edits.
  useEffect(() => {
    validateInnerData();
  }, [
    validationTrigger,
    laserLinkType,
    editLaserLinkItem?.campaign_id,
    editLaserLinkItem?.receipient_type,
    editLaserLinkItem?.notify_after,
    editLaserLinkItem?.notify_for_page,
    editLaserLinkItem?.notify_page_after,
    getDocFiles,
    batchFile
  ]);

  useEffect(() => {
    validateInnerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laserLinkType, editLaserLinkItem?.campaign_id]);

  // Domain dropdown options, sorted by name.
  const domainOptions = (getDomains ?? [])
    .slice()
    .sort((a, b) =>
      (a?.master_domain?.name ?? "").localeCompare(b?.master_domain?.name ?? "")
    )
    .map((item) => ({
      ...item,
      title: item?.master_domain?.name,
      value: item?.master_domain?.name,
    }));

  const defaultDomainName = getDomains?.find(
    (item) => item.default === true
  )?.master_domain?.name;

  // Always resolve to a domain that actually exists in the option list so the
  // <select> stays a valid controlled input. A stored domain that is blank or no
  // longer assigned to the user would otherwise leave the select uncontrolled,
  // which is what forced the "pick it twice" behaviour and let links save with
  // the fallback view.* domain.
  const resolvedDomainName = domainOptions.some(
    (o) => o.value === editLaserLinkItem?.domain
  )
    ? editLaserLinkItem.domain
    : defaultDomainName || domainOptions[0]?.value || "";

  // Backfill the resolved domain into the item so the save payload always carries
  // a real domain (never blank).
  useEffect(() => {
    if (!editLaserLinkItem) return;
    if (!domainOptions.length) return;
    if (!resolvedDomainName) return;
    if (editLaserLinkItem.domain === resolvedDomainName) return;
    updateEditLaserLinkData({ ...editLaserLinkItem, domain: resolvedDomainName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editLaserLinkItem, resolvedDomainName, domainOptions.length]);

  return (
    <Row className="laserlink-main-sec">
      <Col md={6}>
        {batchStatus == true && (
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2">
            <label className={`custom-file-upload flex-grow-1 mb-0 ${errorListData.includes("batch_link") || errorListData.includes("batch_file") ? "has-error" : ""}`}>
              <input
                type="file"
                className="w-100"
                accept=".xls,.xlsx"
                onChange={(e) => handleValidateBatchFile(e.target.files[0])}
              />
            </label>
            <CustomTooltip
              placement="left"
              title={
                <p className="p-1 text-light">Please upload an excel file containing columns for: LaserLINK Title, First Name, Last Name, and Email Address so that you will know exactly which LaserLINK has been configured for each of your recipients. Link Title is required, needs to be UNIQUE, and located in the first column of your spreadsheet. Avoid using tabs, commas, semicolons or any other special characters in the LaserLINK Title. Each row should contain one recipient.</p>
              }
            >
            {/* tooltip trigger: */}
          <svg xmlns="http://www.w3.org/2000/svg" className="ml-10" width="18" height="19" viewBox="0 0 18 19" fill="none">
            <defs>
              <mask id="mask0_2080_1542" maskUnits="userSpaceOnUse" x="0" y="0" width="18" height="19">
                <rect y="0.357178" width="18" height="18" fill="#D9D9D9" />
              </mask>
            </defs>

            <g mask="url(#mask0_2080_1542)">
              <path 
                d="M8.24975 13.1069H9.74975V8.60693H8.24975V13.1069ZM8.99975 7.10693C9.21225 7.10693 9.39038 7.03506 9.53413 6.89131C9.67788 6.74756 9.74975 6.56943 9.74975 6.35693C9.74975 6.14443 9.67788 5.96631 9.53413 5.82256C9.39038 5.67881 9.21225 5.60693 8.99975 5.60693C8.78725 5.60693 8.60913 5.67881 8.46538 5.82256C8.32163 5.96631 8.24975 6.14443 8.24975 6.35693C8.24975 6.56943 8.32163 6.74756 8.46538 6.89131C8.60913 7.03506 8.78725 7.10693 8.99975 7.10693ZM8.99975 16.8569C7.96225 16.8569 6.98725 16.6601 6.07476 16.2663C5.16226 15.8726 4.36851 15.3382 3.69351 14.6632C3.01851 13.9882 2.48413 13.1944 2.09038 12.2819C1.69663 11.3694 1.49976 10.3944 1.49976 9.35693C1.49976 8.31943 1.69663 7.34443 2.09038 6.43193C2.48413 5.51943 3.01851 4.72568 3.69351 4.05068C4.36851 3.37568 5.16226 2.84131 6.07476 2.44756C6.98725 2.05381 7.96225 1.85693 8.99975 1.85693C10.0373 1.85693 11.0123 2.05381 11.9248 2.44756C12.8373 2.84131 13.631 3.37568 14.306 4.05068C14.981 4.72568 15.5154 5.51943 15.9091 6.43193C16.3029 7.34443 16.4998 8.31943 16.4998 9.35693C16.4998 10.3944 16.3029 11.3694 15.9091 12.2819C15.5154 13.1944 14.981 13.9882 14.306 14.6632C13.631 15.3382 12.8373 15.8726 11.9248 16.2663C11.0123 16.6601 10.0373 16.8569 8.99975 16.8569ZM8.99975 15.3569C10.6748 15.3569 12.0935 14.7757 13.256 13.6132C14.4185 12.4507 14.9998 11.0319 14.9998 9.35693C14.9998 7.68193 14.4185 6.26318 13.256 5.10068C12.0935 3.93818 10.6748 3.35693 8.99975 3.35693C7.32475 3.35693 5.90601 3.93818 4.74351 5.10068C3.58101 6.26318 2.99976 7.68193 2.99976 9.35693C2.99976 11.0319 3.58101 12.4507 4.74351 13.6132C5.90601 14.7757 7.32475 15.3569 8.99975 15.3569Z"
                fill="#828282"
              />
            </g>
          </svg>
          </CustomTooltip>
          </div>
          {errorListData?.includes("batch_link") && (
            <small className="text-danger d-block fs-6 mt-1">Please upload a valid .xls or .xlsx file.</small>
          )}
        </div>
        )}
        <Row className="mb-3">
          <Col md={4} className="laser-imgs-center">
            {/* {batchStatus != true && ( */}
              <a
                href={editLaserLinkItem?.tracking_link || editLaserLinkItem?.viewer}
                target="_blank"
                className="laser-img-popup"
              >
                <img
                  src={editLaserLinkItem?.thumb || editLaserLinkItem?.thumbnail || noImage}
                  alt={editLaserLinkItem?.name}
                  width="100%"
                />
              </a>
            {/* )} */}
          </Col>
          <Col md={8}>
            <Form.Check
              className="laser-setting-popup"
              inline
              label="Create individual LaserLINK"
              name="link_type"
              type={"radio"}
              id={`laserLINK-individual-radio`}
              value="individual"
              checked={laserLinkType == "individual"}
              onChange={() => {
                setLaserLinkType("individual");
                updateEditLaserLinkData({
                  ...editLaserLinkItem,
                  campaign: null,
                  campaign_id: null,
                });
              }}
            />
            <Form.Check
              className="laser-setting-popup"
              inline
              label="Create public LaserLINK"
              name="link_type"
              type={"radio"}
              id={`laserLINK-public-radio`}
              value="public"
              checked={laserLinkType == "public"}
              title="This type of LaserLINK is designed to be shared via social media."
              onChange={() => setLaserLinkType("public")}
            />
            {laserLinkType == "public" && (
              <div className={`custom-tags campaign-input-container ${errorListData?.includes("campaign") ? "has-error" : ""}`}>
                <CustomTagsInput
                  tags={
                    editLaserLinkItem?.campaign != undefined
                      ? [editLaserLinkItem?.campaign]
                      : []
                  }
                  placeholderText="Campaign..."
                  limit={1}
                  handleRemoveTags={() => handleUpdateCampaign()}
                  autoSubmit={
                    campaignListData?.filter((item) =>
                      item?.name.includes(filteredCampaign)
                    )?.length > 0
                  }
                  allowExplicitSubmit
                  triggerKeyDownEvent={(value) => {
                    if (editLaserLinkItem?.campaign == undefined) {
                      setFilteredCampaign(value);
                    }
                  }}
                  handleAddTags={(value) => {
                    handleUpdateCampaign(value);
                  }}
                />
                {filteredCampaign != "" && (
                  <ul className="campaign-list">
                    {campaignListData
                      ?.filter((item) => item?.name.includes(filteredCampaign))
                      .map((item, index) => (
                        <li
                          key={item.id ?? index}
                          onClick={() => {
                            setFilteredCampaign("");
                            handleUpdateCampaign(item.name);
                          }}
                          className="cursor-pointer"
                        >
                          {item.name}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            )}

            <Form.Check
              className="laser-setting-popup mt-2"
              inline
              label="Save As Template"
              name="template"
              type={"checkbox"}
              id={`laserLINK-template-checkbox`}
              checked={editLaserLinkItem?.template}
              onChange={() =>
                updateEditLaserLinkData({
                  ...editLaserLinkItem,
                  template: !editLaserLinkItem?.template,
                })
              }
            />
            <CustomTooltip
              title={<p className="p-1 text-light">To create KDF to KDF linking use this checkbox</p>}
            >
              <Form.Check
                className="laser-setting-popup"
                inline
                label="KDF to KDF Link"
                name="kdf_action_link"
                type={"checkbox"}
                id={`laserLINK-action-checkbox`}
                checked={editLaserLinkItem?.kdf_action_link}
                onChange={() =>
                  updateEditLaserLinkData({
                    ...editLaserLinkItem,
                    kdf_action_link: !editLaserLinkItem?.kdf_action_link,
                  })
                }
              />
            </CustomTooltip>
            <Form.Check
              className="laser-setting-popup"
              inline
              name="set_as_embed"
              type={"checkbox"}
              id={`set_as_embed`}
              value="set_as_embed"
              checked={!!editLaserLinkItem?.set_as_embed}
              onChange={() => {
                const enabling = !editLaserLinkItem?.set_as_embed;
                updateEditLaserLinkData({
                  ...editLaserLinkItem,
                  set_as_embed: enabling,
                  no_privacy_link: enabling || editLaserLinkItem?.privacy_policy_consent !== false,
                });
              }}
              label={"Set as embed"}
            />
          </Col>
        </Row>
        <div className="domain-select-lab">
          <div className="mb-3">
            <label className="">Domain</label>
            <Select
              className="domain-select"
              classList=""
              name="domain"
              noDefault={true}
              onChange={(e) => handleOnchangeEvent(e)}
              defaultValue={resolvedDomainName}
              optionList={domainOptions}
            />
          </div>
          <div className="align-items-center hook-second-inp laserlink-custom-tags mb-3">
            <label className="">Tags:
              <CustomTooltip title={<p className="p-1 text-light">You can add a tag by pressing enter, tab, delimiting with comma or clicking outside the tags area</p>}>
                {/* <FontAwesomeIcon icon={faQuestion} className="custom-tag-icon mx-2" /> */}
                <img src="/icons/info.svg" />
              </CustomTooltip>
            </label>
          <div className="label-tags-container">
            <TagChipEditor
              tags={editLaserLinkItem?.tags || []}
              onChange={handleTagsChange}
              placeholderText="Add a new tag..."
              minWidthCh={15}
              collapsible
            />
            </div>
          </div>

          <div className="hook mb-3">
            <label className="">Hook:
            <CustomTooltip
              title={
                <p className="p-1 text-light">Insert text here that will be displayed along with the KDF title when you share LaserLINX via social media. Use this text to encourage recipients to click the LaserLINK you shared with them.</p>
              }
            >
              {/* <FontAwesomeIcon
                icon={faQuestion}
                className="custom-tag-icon mt-2 mx-2"
              /> */}
              <img src="/icons/info.svg" />
            </CustomTooltip>
            </label>
            <Input
              name="seo_description"
              value={editLaserLinkItem?.seo_description}
              onChange={(e) => handleOnchangeEvent(e)}
              className="hookInput"
            />
          </div>
        </div>
      </Col>
      <Col md={6}>
        {editLaserLinkItem?.doc_files_languages?.length > 1 ? (
          <div className="d-flex align-items-center mb-3 language-select-wrap">
            <span className={`me-3 ${styles.languageLabel}`}>Language:</span>
            <div className="language-button flex-grow-1">
              <CustomDropdown
                options={editLaserLinkItem.doc_files_languages?.map((lang) => {
                return {
                  value: lang?.language?.locale,
                  label: lang?.language?.name,
                  labelTitle: lang?.count_live_files == 0 ? 'This language edition needs to be published before it can be shared' : null,
                  disable: lang?.count_live_files == 0,
                  class: lang?.language?.class,
                };
              })}
              name={"language"}
              onSelect={(e) =>
                handleOnchangeEvent({ target: { name: "language", value: e } })
              }
              selected={
                editLaserLinkItem?.language?.locale != undefined
                  ? editLaserLinkItem?.language.locale
                  : (editLaserLinkItem?.language != undefined ? editLaserLinkItem?.language : editLaserLinkItem.languages)
              }
              placeholder="Select an Language"
            />
            </div>
          </div>
        ) : editLaserLinkItem?.doc_files_languages?.length == 1 ? (
          <div className="d-flex language-span">
            <span className={`me-3 ${styles.languageLabel}`}>Language:</span>
            <span>
              <div className={`p-2 mr-1 ${editLaserLinkItem?.doc_files_languages[0].language?.class}`}></div>
              {editLaserLinkItem?.doc_files_languages[0].language?.name}
            </span>
          </div>
        ) : (
          ""
        )}
        <div className="mt-3 mb-3 d-flex landing-para">
          <p className={`me-1 ${styles.landingPageLabel}`}>Landing page:</p>
          <span className="landing-para-span">
            <CustomTooltip
              title={<p className="p-1 text-light">Please enter a page between 1 and {getDocFiles[0]?.num_pages ?? 0
              }</p>}
            >
              <input
                className={`edit-box-input ${(errorListData?.includes('landing_page') || errorListData?.includes('landing_page_gt_optin')) ? 'inputError' : ''}`}
                type="number"
                value={editLaserLinkItem?.landing_page ?? ""}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const val = e.target.value;
                  let errorToUpdate = [...(errorListData || [])];
                  if (val == 0 || (val > (getDocFiles[0]?.num_pages || 0))) {
                    if (!errorToUpdate.includes("landing_page")) errorToUpdate.push("landing_page");
                  } else {
                    errorToUpdate = errorToUpdate.filter((error) => error !== "landing_page");
                  }
                  const optInPage = getOptInPageNumber();
                  const landingNum = Number(val);
                  const landingGtOptIn = optInPage != null && optInPage >= 1 && !Number.isNaN(landingNum) && landingNum > optInPage;
                  if (landingGtOptIn) {
                    if (!errorToUpdate.includes("landing_page_gt_optin")) errorToUpdate.push("landing_page_gt_optin");
                  } else {
                    errorToUpdate = errorToUpdate.filter((error) => error !== "landing_page_gt_optin");
                  }
                  updateErrorStatus(errorToUpdate);
                  updateEditLaserLinkData({
                    ...editLaserLinkItem,
                    landing_page: val,
                  });
                }}
              />
            </CustomTooltip> &nbsp;
            / {getDocFiles[0]?.num_pages ?? 0}
          </span>
        </div>
        {errorListData?.includes("landing_page_gt_optin") && (
          <p className="text-danger mt-0 mb-3">
            Opt-in is enabled for this KDF. You cannot set the landing page greater than the opt-in page value.
          </p>
        )}
        <div className="d-flex align-items-center">
          <Form.Check
            className="laser-setting-popup"
            inline
            name="show_toolbar"
            type={"checkbox"}
            id={`show_toolbar`}
            value="show_toolbar"
            checked={!!editLaserLinkItem?.show_toolbar}
            disabled={disableSettings?.disable_on === "cyoa"}
            onChange={(element) =>
              updateEditLaserLinkData({
                ...editLaserLinkItem,
                show_toolbar: !!!editLaserLinkItem?.show_toolbar,
              })
            }
            label={<span className="cursor-pointer">
                  Show toolbar {disableSettings?.disable_on === "cyoa" && (
            <CustomTooltip
              placement="left"
              title={
                <p className="p-1 text-light">Toolbar is hidden when CYOA is enabled.</p>
              }
            >
              <img src="/icons/info.svg" alt="" />
            </CustomTooltip>
          )} </span>}
          />
        </div>
        <Form.Check
          className="laser-setting-popup"
          inline
          name="show_thumbnail"
          type={"checkbox"}
          id={`show_thumbnail`}
          value="show_thumbnail"
          checked={disableSettings?.disable_on === "cyoa" ? false : !!editLaserLinkItem?.show_thumbnail}
          disabled={disableSettings?.disable_on === "cyoa"}
          onChange={(element) =>
            updateEditLaserLinkData({
              ...editLaserLinkItem,
              show_thumbnail: !!!editLaserLinkItem?.show_thumbnail,
            })
          }
          label={<span className="cursor-pointer">
                Show thumbnail navigation {disableSettings?.disable_on === "cyoa" && (
            <CustomTooltip
              placement="left"
              title={
                <p className="p-1 text-light">Thumbnail navigation is hidden when CYOA is enabled.</p>
              }
            >
              <img src="/icons/info.svg" alt="" />
            </CustomTooltip>
          )} </span>}
        />
        <div>
          <Form.Check
            className="laser-setting-popup"
            inline
            name="privacy_policy_consent"
            type={"checkbox"}
            id={`privacy_policy_consent`}
            value="privacy_policy_consent"
            checked={editLaserLinkItem?.privacy_policy_consent !== false}
            onChange={() => {
              const enabling = editLaserLinkItem?.privacy_policy_consent === false;
              updateEditLaserLinkData({
                ...editLaserLinkItem,
                privacy_policy_consent: enabling,
                ...(enabling && { no_privacy_link: true }),
              });
            }}
            label={<span className="cursor-pointer">Show cookie consent pop-up</span>}
          />
          <small className="text-muted d-block mb-2">
            You are the data controller for this LaserLINK. If you turn off the consent pop-up, you must have a lawful basis, such as your recipient's permission, before tracking their activity in this KDF.
          </small>
        </div>
        <Form.Check
          className="laser-setting-popup"
          inline
          name="enable_download"
          type={"checkbox"}
          id={`enable_download`}
          value="enable_download"
          checked={disableSettings?.disable_on === "cyoa" ? false : !!editLaserLinkItem?.enable_download}
          disabled={disableSettings?.disable_on === "cyoa"}
          onChange={(element) =>
            updateEditLaserLinkData({
              ...editLaserLinkItem,
              enable_download: !!!editLaserLinkItem?.enable_download,
            })
          }
          label={"Enable download"}
        />
        <Form.Check
          className="laser-setting-popup"
          inline
          name="disable_copy"
          type={"checkbox"}
          id={`disable_copy`}
          value="disable_copy"
          checked={!editLaserLinkItem?.disable_copy}
          onChange={(element) =>
            updateEditLaserLinkData({
              ...editLaserLinkItem,
              disable_copy: !!!editLaserLinkItem?.disable_copy,
            })
          }
          label={
            <span className="cursor-pointer">
              Allow recipients to copy text{" "}
            </span>
          }
        />
        <Form.Check
          className="laser-setting-popup"
          inline
          name="allow_resume"
          type={"checkbox"}
          id={`allow_resume`}
          value="allow_resume"
          checked={editLaserLinkItem?.allow_resume !== false}
          onChange={() =>
            updateEditLaserLinkData({
              ...editLaserLinkItem,
              allow_resume: editLaserLinkItem?.allow_resume === false,
            })
          }
          label={<span className="cursor-pointer">Offer resume after refresh</span>}
        />
        <Form.Check
          className="laser-setting-popup"
          inline
          name="has_optin"
          type={"checkbox"}
          id={`has_optin`}
          value="has_optin"
          checked={!!editLaserLinkItem?.has_optin}
          onChange={(element) =>
            updateEditLaserLinkData({
              ...editLaserLinkItem,
              has_optin: !!!editLaserLinkItem?.has_optin,
            })
          }
          label={"Enable opt-in"}
        />
        <Form.Check
          className="laser-setting-popup"
          inline
          name="disable_scroll"
          type={"checkbox"}
          id={`disable_scroll`}
          value="disable_scroll"
          checked={editLaserLinkItem?.disable_scroll ?? false}
          onChange={(element) => {
            let disableMessage =
              editLaserLinkItem?.disable_scroll == false
                ? {
                  disable_on: "cyoa",
                  disable_message: "This document will be scrollable in",
                }
                : {};
            updateEditLaserLinkData({
              ...editLaserLinkItem,
              disable_scroll:
                editLaserLinkItem?.disable_scroll == true ? false : true,
              disable_settings: disableMessage,
            });
          }}
          label={"Control Journey"}
        />
        {!!editLaserLinkItem?.disable_scroll && (
          <div className="ms-3">
            <Form.Check
              className="laser-setting-popup"
              inline
              name="disable_scroll"
              type={"radio"}
              id={`notify-kdf-radio-notify_when_page_cyoa`}
              value="notify_when_page_cyoa"
              checked={disableSettings?.disable_on === "cyoa"}
              onChange={(element) => {
                updateEditLaserLinkData({
                  ...editLaserLinkItem,
                  enable_download: false,
                  show_thumbnail: false,
                  disable_settings: {
                    ...disableSettings,
                    disable_on: "cyoa",
                  },
                });
              }}
              label={
                <span className="cursor-pointer">
                  CYOA to proceed{" "}
                  <CustomTooltip
                    placement="left"
                    title={
                      <p className="p-1 text-light">This setting disables scrolling completely. Recipients will need to navigate using clickable hotspots and images that you place on each page. If you allow recipients to revisit pages, keep in mind that those clicks will skew your stats.</p>
                    }
                  >
                    {/* <FontAwesomeIcon
                      icon={faQuestion}
                      className="custom-tag-icon mx-2"
                    /> */}
                    <img src="/icons/info.svg" />
                  </CustomTooltip>
                </span>
              }
            />
            <div className="d-flex align-items-center">
              <Form.Check
                className="laser-setting-popup"
                inline
                name="disable_scroll"
                type={"radio"}
                id={`notify-kdf-radio-after_seconds`}
                value="after_seconds"
                checked={disableSettings?.disable_on === "seconds"}
                onChange={(element) => {
                  updateEditLaserLinkData({
                    ...editLaserLinkItem,
                    disable_settings: {
                      ...disableSettings,
                      disable_on: "seconds",
                    },
                  });
                }}
                label={
                  <span className="cursor-pointer">
                    Delay for &nbsp;
                    <CustomTooltip title={<p className="p-1 text-light">Please enter the time in seconds</p>}>
                      <input
                        className="edit-box-input"
                        type="number"
                        min={1}
                        value={disableSettings?.disable_for ?? ""}
                        // ref={receipientTypeNotify}
                        name="disable_seconds"
                        onKeyDown={handleDelaySecondsKeyDown}
                        onWheel={handleDelaySecondsWheel}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const sanitizedValue = sanitizeDelaySecondsInput(
                            e.target.value
                          );

                          updateEditLaserLinkData({
                            ...editLaserLinkItem,
                            disable_settings: {
                              ...editLaserLinkItem.disable_settings,
                              disable_for: sanitizedValue,
                            },
                          });
                        }}
                        onBlur={handleDelaySecondsBlur}
                      /> 
                    </CustomTooltip> &nbsp;
                    seconds 
                    <span>
                      {disableSettings?.disable_on ?
                        "" :"seconds"}
                    </span>
                    {disableSettings?.disable_on ?
                      "" :"seconds"}
                    <CustomTooltip
                      placement="left"
                      title={
                        <p className="p-1 text-light">Scrolling will be disabled for X seconds after the landing page loads so that recipients can not bypass the first page of your content.</p>
                      }
                    >
                      {/* <FontAwesomeIcon
                        icon={faQuestion}
                        className="custom-tag-icon mx-2"
                      /> */}
                      <img src="/icons/info.svg" />
                    </CustomTooltip>
                  </span>
                }
              />
            </div>

            {shouldShowRecipientNotification && (
              <>
                <p className="cursor-pointer re-notify mb-2">
                  Recipient's notification{" "}
                  <CustomTooltip
                    placement="left"
                    title={
                      <p className="p-1 text-light">The message you enter here will be displayed if the recipient tries to scroll prematurely.</p>
                    }
                  >
                    <img src="/icons/info.svg" />
                  </CustomTooltip>
                </p>
                <input
                  className="edit-box-input rec-notify-laser  mb-3 w-100"
                  type="text"
                  value={disableSettings?.disable_message ?? ""}
                  onChange={(e) =>
                    updateEditLaserLinkData({
                      ...editLaserLinkItem,
                      disable_settings: {
                        ...disableSettings,
                        disable_message: e.target.value,
                      },
                    })
                  }
                />
              </>
            )}
          </div>
        )}
        {!!editLaserLinkItem?.set_as_embed && (
          <>
            <Form.Check
              className="laser-setting-popup"
              inline
              name="enable_transparent_bg"
              type={"checkbox"}
              id={`enable_transparent_bg`}
              value="enable_transparent_bg"
              checked={!!editLaserLinkItem?.enable_transparent_bg}
              onChange={() =>
                updateEditLaserLinkData({
                  ...editLaserLinkItem,
                  enable_transparent_bg: !!!editLaserLinkItem?.enable_transparent_bg,
                })
              }
              label={"Embed - No background"}
            />
            <Form.Check
              className="laser-setting-popup"
              inline
              name="enable_shadow_box"
              type={"checkbox"}
              id={`enable_shadow_box`}
              value="enable_shadow_box"
              checked={!!editLaserLinkItem?.enable_shadow_box}
              onChange={() =>
                updateEditLaserLinkData({
                  ...editLaserLinkItem,
                  enable_shadow_box: !!!editLaserLinkItem?.enable_shadow_box,
                })
              }
              label={"Embed - No shadow"}
            />
            <Form.Check
              className="laser-setting-popup"
              inline
              name="no_privacy_link"
              type={"checkbox"}
              id={`no_privacy_link`}
              value="no_privacy_link"
              checked={!!editLaserLinkItem?.no_privacy_link || editLaserLinkItem?.privacy_policy_consent !== false}
              disabled={editLaserLinkItem?.privacy_policy_consent !== false}
              onChange={() =>
                updateEditLaserLinkData({
                  ...editLaserLinkItem,
                  no_privacy_link: !!!editLaserLinkItem?.no_privacy_link,
                })
              }
              label={"Embed - No Privacy Link"}
            />
          </>
        )}
      </Col>
    </Row>
  );
};
export default LaserLINKSettingsTab;
